'use client';

import { BackArrow } from '@ahamove/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ActionIcon,
  Box,
  Button,
  FocusTrap,
  Group,
  PinInput,
  rem,
  Space,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useForm, Controller } from 'react-hook-form';
import * as z from 'zod';
import { useAuth } from '../../auth-provider';
import { AuthStep } from '../../lib/constants';
import { authLocales, createTranslations, type Locales } from '../../locales';
import { useAuthStore } from '../../store/auth-store-provider';

const passcodeSchema = z.object({
  passcode: z.string().length(6, 'Passcode must be 6 digits'),
});

type PasscodeFormValues = z.infer<typeof passcodeSchema>;
type Props = {
  lang?: keyof Locales;
  onChangeTabPasscode: (tab: string) => void;
  onChangePasscode: (passcode: string) => void;
};
export default function CreatePasscodeForm({
  lang = 'vi',
  onChangeTabPasscode,
  onChangePasscode,
}: Props) {
  const tAuth = createTranslations({ locales: authLocales, lang });

  const { apiError, activeAuthStep, setActiveAuthStep } = useAuthStore(
    (state) => state
  );

  const { handleSkipCreatePasscode } = useAuth();

  const { loading } = useAuth();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasscodeFormValues>({
    resolver: zodResolver(passcodeSchema),
    defaultValues: {
      passcode: '',
    },
  });

  const onSubmit = (data: PasscodeFormValues) => {
    // handleActivateUser(data.passcode);
    onChangePasscode(data.passcode);
    onChangeTabPasscode('confirm');
  };

  const handleBack = () => {
    reset();
    setActiveAuthStep(AuthStep.PhoneNumber);
  };

  const handlePasscodeComplete = (value: string) => {
    if (value.length === 6) {
      // handleActivateUser(value);
      onChangePasscode(value);
      onChangeTabPasscode('confirm');
    }
  };

  return (
    <Box
      display="flex"
      w="100%"
      flex={1}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => reset()}
      pb="xs"
      style={{ flexDirection: 'column' }}
    >
      <Stack align="stretch" flex={1} gap="sm">
        <Group wrap="nowrap" hiddenFrom="sm">
          <ActionIcon
            size={rem(44)}
            radius="xl"
            variant="subtle"
            color="neutral"
            aria-label="Back"
            my={rem(-10)}
            ml={rem(-10)}
            onClick={handleBack}
          >
            <BackArrow width={24} height={24} />
          </ActionIcon>
        </Group>
        <Title order={2}>{tAuth('createPasscodeTitle')}</Title>
        <Text size="sm" c="neutral.5" fw={500}>
          {tAuth('createPasscodeDescription')}
        </Text>
        <Stack gap={0} w="100%" mt="sm">
          <FocusTrap
            active={activeAuthStep === AuthStep.PassCode || !!apiError}
          >
            <Controller
              name="passcode"
              control={control}
              render={({ field: { onChange, value } }) => (
                <PinInput
                  disabled={loading}
                  length={6}
                  gap={rem(8)}
                  type="number"
                  inputMode="numeric"
                  size="lg"
                  placeholder=""
                  mask
                  manageFocus
                  value={value}
                  onChange={onChange}
                  onComplete={handlePasscodeComplete}
                  error={!!errors.passcode || !!apiError}
                  styles={{
                    root: {
                      '--mantine-color-gray-4':
                        'var(--mantine-color-neutral-0)',
                    },
                    input: {
                      backgroundColor: 'var(--mantine-color-neutral-0)',
                    },
                  }}
                />
              )}
            />
          </FocusTrap>
          <Text fw={500} size="sm" c="red.5" my={rem(8)} mih={rem(20)}>
            {errors.passcode?.message ??
              apiError?.description ??
              apiError?.title}
          </Text>
          <Group p={0} align="center" justify="flex-start">
            <Button
              variant="transparent"
              px={0}
              c="primary"
              fw={700}
              onClick={handleSkipCreatePasscode}
            >
              {tAuth('skip')}
            </Button>
          </Group>
        </Stack>

        <Space h={rem(32)} w="100%" />

        {/* <Center mt="auto">
          <Anchor size="sm" fw={600} onClick={handleBack} c="red.6">
            {tAuth('changePhoneNumber')}
          </Anchor>
        </Center> */}
      </Stack>
    </Box>
  );
}
